<template>
    <div class="row">
        <div class="col-12 text-center" v-if="!submitSuccess && !submitError">
            <p>Request a case study or simply give us a shout to see how we can support you.</p>
        </div>
        <div class="col-12">
            <div v-if="!submitSuccess && !submitError">
                <form class="form">
                    <div class="row justify-content-center mt-30">
                        <div class="col-12 col-6-md">
                                <input-text
                                    input-type="text"
                                    name="fullname"
                                    label="Your name"
                                    placeholder="John Smith"
                                    v-model="fullname"
                                    :validation-state="v$.fullname"
                                >
                                </input-text>
                                <input-text
                                    input-type="text"
                                    name="company"
                                    label="Which business are you from?"
                                    placeholder="Business Name Ltd"
                                    v-model="company"
                                    :validation-state="v$.company"
                                >
                                </input-text>
                                <input-text
                                    input-type="text"
                                    name="email"
                                    label="Your email address"
                                    placeholder="john.smith@business.com"
                                    v-model="email"
                                    :validation-state="v$.email"
                                >
                                </input-text>
                                <input-text
                                    input-type="text"
                                    name="telephone"
                                    label="Best number to contact you on"
                                    placeholder="07999 999999"
                                    v-model="telephone"
                                    :validation-state="v$.telephone"
                                >
                                </input-text>
                                <input-text
                                    input-type="textarea"
                                    name="message"
                                    label="Your message"
                                    placeholder="Tell us a bit about your business and how we can help"
                                    v-model="message"
                                    :validation-state="v$.message"
                                >
                                </input-text>
                                <checkbox
                                    name="privacy"
                                    form-value="yes"
                                    v-model="privacy"
                                    :validation-state="v$.privacy"
                                    class="mt-20"
                                >
                                    <span class="text-xs">By proceeding, I agree for my personal data to be used in accordance with the <a href="/privacy-policy" target="_blank">Privacy
                                    Policy</a> and to the Terms and Conditions of this website.</span><br>
                                </checkbox>
                        </div>
                    </div>
                    <div class="row justify-content-center text-center mt-20">
                        <div class="col-12">
                            <button type="button" class="button button--cta text-center" @click="validate">Get in touch</button>
                        </div>
                    </div>
                </form>
            </div>
            <div class="submit-success pt-30" v-if="submitSuccess">
                <div class="row justify-content-center mt-30 text-center">
                    <div class="col-12 col-6-md">
                        <h4 class="submit-success__title">
                            Thank you for your interest.
                        </h4>
                        <p>
                            <strong>Someone from the regit.cars team will be in touch very shortly.</strong>
                        </p>
                    </div>
                </div>
            </div>

            <div class="pt-30" v-if="submitError">
                <div class="row justify-content-center mt-30 text-center">
                    <div class="col-12 col-6-md">
                        <h4 class="submit-error__title">
                            Oops! Something went wrong on our end. Please try again or let us know if the problem continues.
                        </h4>
                    </div>
                    <div class="col-12 mt-20">
                        <button type="button" class="button button--cta text-center" @click="goBack">Go Back</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue';
import useVuelidate from '@vuelidate/core';
import {required, email, sameAs, helpers, maxLength} from '@vuelidate/validators';
import {phone} from '../modules/regitValidators';
import axiosClient from '../modules/axiosClient';
import Checkbox from './global/checkbox.vue';

export default defineComponent({
    props: {

    },
    components: {
        'checkbox': Checkbox,

    },
    data() {
        return {
            submitSuccess: false,
            submitError: false,
            uuid: '0',

            // form data
            fullname: '',
            company: '',
            email: '',
            telephone: '',
            message: '',
            privacy: false,
            vuelidateExternalResults: {
                fullname: [],
                company: [],
                email: [],
                telephone: [],
                message: [],
                privacy: [],
            },
        };
    },
    setup() {
        const externalResults = ref();
        return {
            externalResults,
            v$: useVuelidate({$autoDirty: true}),
        };
    },
    validations() {
        return {
            fullname: {
                required: helpers.withMessage('Please enter your full name', required),
            },
            company: {
                required: helpers.withMessage('Please enter your company name', required),
            },
            email: {
                required: helpers.withMessage('Please enter your email', required),
                email,
            },
            telephone: {
                required: helpers.withMessage('Please enter your telephone', required),
                phone: helpers.withMessage('Please enter a valid telephone', phone),
            },
            message: {
                required: helpers.withMessage('Please enter your message', required),
                maxLength: helpers.withMessage('The maximum message length is 500 characters.', maxLength(500)),
            },
            privacy: {
                required: helpers.withMessage('Please accept our T&C', required),
                sameAs: helpers.withMessage('Please accept our T&C', sameAs(true)),
            },
        };
    },
    methods: {
        async validate() {
            this.v$.$touch();

            const validationPassed = await this.v$.$validate();
            if (validationPassed) {
                await this.submit();
            }
        },
        async submit() {
            try {
                const payload = {
                    'fullname': this.fullname,
                    'company': this.company,
                    'email': this.email,
                    'telephone': this.telephone,
                    'message': this.message,
                    'privacy': this.privacy,
                };

                await axiosClient.post('/about-save-contact', payload);
                this.submitSuccess = true;
                window.sendAnalyticsEvent('Conversion Tracking', 'Trade About Us', 'Contact Form');
            } catch (error: any) {
                if (error.response && error.response.data && error.response.data.errors) {
                    console.log('Validation errors', error.response.data.errors);
                    Object.assign(this.vuelidateExternalResults, {
                        fullname: error.response.data.errors.fullname ?? [],
                        company: error.response.data.errors.company ?? [],
                        email: error.response.data.errors.email ?? [],
                        telephone: error.response.data.errors.telephone ?? [],
                        message: error.response.data.errors.message ?? [],
                        privacy: error.response.data.errors.privacy ?? [],
                    });
                } else {
                    this.submitError = true;
                    console.error('API call error: ', error);
                }
            }
        },
        goBack() {
            this.submitError = false;
            // reset form
            this.fullname = '';
            this.company = '';
            this.email = '';
            this.telephone = '';
            this.message = '';
            this.privacy = false;
            this.v$.$reset();
        },
    },
});
</script>
